import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { chu, gom, gonSo as gonSoThuan, mocTruc, phanO, so } from '../../nghiep-vu/bieu-do';
import type { AskDisplay } from '../../types';

/**
 * Vẽ kết quả truy vấn — SVG thuần, không thư viện (port `components/BieuDo.tsx` native).
 *
 * Không thư viện biểu đồ: `recharts` ~450 KB, `chart.js` ~200 KB cho một phần phụ cạnh bảng và lời văn. Mười tám hình vẽ
 * từ những con số đã tính sẵn — không kéo thả, không hoạt hình — `<svg>` làm được hết.
 *
 * Mọi hình đều có `<title>`: biểu đồ nói được XU HƯỚNG và bỏ mất GIÁ TRỊ CHÍNH XÁC; `<title>` cho con số ngay tại chỗ, và
 * là thứ trình đọc màn hình đọc lên.
 *
 * Màu theo CHỈ SỐ, không theo tên nhóm: tên nhóm là dữ liệu của khách hàng, bảng màu suy từ nó sẽ đổi mỗi khi dữ liệu đổi.
 */

/**
 * Mười màu chuỗi qua biến token (thuộc tính SVG `fill`/`stroke` không nhận class, và biến tự đổi theo chủ đề). Xếp để
 * hai chỉ số LIỀN NHAU luôn khác tông rõ rệt.
 */
const MAU = [
  'var(--color-sky-600)',
  'var(--color-orange-600)',
  'var(--color-teal-600)',
  'var(--color-violet-600)',
  'var(--color-success-600)',
  'var(--color-danger-600)',
  'var(--color-primary-600)',
  'var(--color-warning-600)',
  'var(--color-violet-500)',
  'var(--color-teal-500)',
] as const;

const mau = (i: number): string => MAU[i % MAU.length] ?? MAU[0];

type Hang = Record<string, unknown>;

export type Dang = {
  dang: string;
  x?: string | null;
  y?: string | null;
  loat?: string | null;
  kich_thuoc?: string | null;
  chi_tieu?: string[];
  vi_sao?: string;
  theoYeuCau?: boolean;
  /** Những dạng VẼ ĐƯỢC với dữ liệu này — máy chủ tính, giao diện không đoán. */
  hopLe?: string[];
  /** Trục cho từng dạng hợp lệ, gửi sẵn để đổi dạng không phải hỏi lại mạng. */
  truc?: Record<string, Partial<Dang>>;
};

/** Định dạng số của biểu đồ — đi theo ngôn ngữ, đơn vị gọn lấy từ catalog. */
const DinhDangCtx = createContext<{ gon: (n: number) => string; day: (n: number) => string }>({
  gon: (n) => String(n),
  day: (n) => String(n),
});
const useDinhDang = () => useContext(DinhDangCtx);

// ── Khung chung ────────────────────────────────────────────────────────────

const W = 720;
const H = 260;
const LE = { tren: 12, phai: 14, duoi: 34, trai: 56 };

function Truc({
  mocY,
  cao,
  rong,
  dinhDang,
}: {
  mocY: number[];
  cao: number;
  rong: number;
  dinhDang?: (n: number) => string;
}) {
  const { gon } = useDinhDang();
  const dd = dinhDang ?? gon;
  const max = mocY[mocY.length - 1] || 1;
  return (
    <g>
      {mocY.map((m) => {
        const y = LE.tren + cao - (m / max) * cao;
        return (
          <g key={m}>
            {/* Lưới nhạt, không đậm: nó là thứ để ĐỐI CHIẾU, không phải nội
                dung. Lưới đậm ngang bằng dữ liệu thì mắt đọc lưới trước. */}
            <line x1={LE.trai} x2={LE.trai + rong} y1={y} y2={y} stroke="currentColor"
                  className="text-content-muted" strokeWidth={1} />
            <text x={LE.trai - 8} y={y + 4} textAnchor="end"
                  className="fill-[var(--text-muted)] text-xs">{dd(m)}</text>
          </g>
        );
      })}
    </g>
  );
}

/** Nhãn trục X có thể rất dài — xoay khi chật, cắt khi vẫn chật. */
function NhanX({ nhan, x, y, rongO }: { nhan: string; x: number; y: number; rongO: number }) {
  const chatChoi = nhan.length * 6 > rongO;
  const cat = nhan.length > 18 ? `${nhan.slice(0, 17)}…` : nhan;
  return (
    <text
      x={x}
      y={y}
      textAnchor={chatChoi ? 'end' : 'middle'}
      transform={chatChoi ? `rotate(-35 ${x} ${y})` : undefined}
      className="fill-[var(--text-secondary)] text-xs"
    >
      {cat}
      <title>{nhan}</title>
    </text>
  );
}

function Chu(props: { children: ReactNode }) {
  return <div className="text-xs text-content-secondary">{props.children}</div>;
}

// ── Các dạng ───────────────────────────────────────────────────────────────

function SoLon({ rows, dang }: { rows: Hang[]; dang: Dang }) {
  const { day } = useDinhDang();
  const v = so(rows[0]?.[dang.y ?? '']);
  return (
    <div className="py-6 text-center">
      <p className="text-3xl font-semibold leading-tight text-content-primary tabular-nums">
        {day(v)}
      </p>
      <p className="mt-1 text-xs text-content-secondary">{dang.y}</p>
    </div>
  );
}

function Cot({ rows, dang, ngang }: { rows: Hang[]; dang: Dang; ngang?: boolean }) {
  const { gon: gonSo, day } = useDinhDang();
  const xk = dang.x ?? '';
  const yk = dang.y ?? '';
  const gt = rows.map((r) => so(r[yk]));
  const max = Math.max(...gt, 0);
  const moc = mocTruc(max);
  const tran = moc[moc.length - 1] || 1;

  if (ngang) {
    const caoO = Math.max(18, Math.min(34, 300 / rows.length));
    const cao = rows.length * caoO;
    const trai = 132;
    return (
      <svg viewBox={`0 0 ${W} ${cao + 28}`} className="w-full" role="img">
        {rows.map((r, i) => {
          const v = so(r[yk]);
          const w = (v / tran) * (W - trai - 60);
          const y = i * caoO;
          return (
            <g key={i}>
              <text x={trai - 8} y={y + caoO / 2 + 4} textAnchor="end"
                    className="fill-[var(--text-primary)] text-xs">
                {chu(r[xk]).length > 20 ? `${chu(r[xk]).slice(0, 19)}…` : chu(r[xk])}
                <title>{chu(r[xk])}</title>
              </text>
              <rect x={trai} y={y + 3} width={Math.max(w, 1)} height={caoO - 8} rx={2} fill={mau(0)}>
                <title>{`${chu(r[xk])}: ${day(v)}`}</title>
              </rect>
              <text x={trai + w + 6} y={y + caoO / 2 + 4}
                    className="fill-[var(--text-secondary)] text-xs tabular-nums">{gonSo(v)}</text>
            </g>
          );
        })}
      </svg>
    );
  }

  const rong = W - LE.trai - LE.phai;
  const cao = H - LE.tren - LE.duoi;
  const rongO = rong / rows.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
      <Truc mocY={moc} cao={cao} rong={rong} />
      {rows.map((r, i) => {
        const v = so(r[yk]);
        const h = (v / tran) * cao;
        const x = LE.trai + i * rongO + rongO * 0.16;
        return (
          <g key={i}>
            <rect x={x} y={LE.tren + cao - h} width={rongO * 0.68} height={Math.max(h, 1)} rx={2}
                  fill={mau(0)}>
              <title>{`${chu(r[xk])}: ${day(v)}`}</title>
            </rect>
            <NhanX nhan={chu(r[xk])} x={LE.trai + i * rongO + rongO / 2} y={H - 14} rongO={rongO} />
          </g>
        );
      })}
    </svg>
  );
}

function CotNhieuLoat({
  rows,
  dang,
  kieu,
}: {
  rows: Hang[];
  dang: Dang;
  kieu: 'nhom' | 'chong' | 'chong100';
}) {
  const { gon: gonSo, day } = useDinhDang();
  const { xs, ls, lay } = gom(rows, dang.x ?? '', dang.loat ?? '', dang.y ?? '');
  const tongCot = xs.map((x) => ls.reduce((s, l) => s + lay(x, l), 0));
  const max = kieu === 'nhom'
    ? Math.max(...xs.flatMap((x) => ls.map((l) => lay(x, l))), 0)
    : Math.max(...tongCot, 0);
  const moc = kieu === 'chong100' ? [0, 25, 50, 75, 100] : mocTruc(max);
  const tran = kieu === 'chong100' ? 100 : (moc[moc.length - 1] || 1);
  const rong = W - LE.trai - LE.phai;
  const cao = H - LE.tren - LE.duoi;
  const rongO = rong / xs.length;

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
        <Truc mocY={moc} cao={cao} rong={rong}
              dinhDang={kieu === 'chong100' ? (n) => `${n}%` : gonSo} />
        {xs.map((x, i) => {
          const goc = LE.trai + i * rongO;
          if (kieu === 'nhom') {
            const w = (rongO * 0.72) / ls.length;
            return (
              <g key={x}>
                {ls.map((l, j) => {
                  const v = lay(x, l);
                  const h = (v / tran) * cao;
                  return (
                    <rect key={l} x={goc + rongO * 0.14 + j * w} y={LE.tren + cao - h}
                          width={Math.max(w - 1, 1)} height={Math.max(h, 1)} fill={mau(j)}>
                      <title>{`${x} · ${l}: ${day(v)}`}</title>
                    </rect>
                  );
                })}
                <NhanX nhan={x} x={goc + rongO / 2} y={H - 14} rongO={rongO} />
              </g>
            );
          }
          const tong = tongCot[i] || 1;
          let day_ = 0;
          return (
            <g key={x}>
              {ls.map((l, j) => {
                const v = lay(x, l);
                const phan = kieu === 'chong100' ? (v / tong) * 100 : v;
                const h = (phan / tran) * cao;
                const y = LE.tren + cao - day_ - h;
                day_ += h;
                return (
                  <rect key={l} x={goc + rongO * 0.16} y={y} width={rongO * 0.68}
                        height={Math.max(h, 0)} fill={mau(j)}>
                    <title>{`${x} · ${l}: ${day(v)}${
                      kieu === 'chong100' ? ` (${((v / tong) * 100).toFixed(1)}%)` : ''
                    }`}</title>
                  </rect>
                );
              })}
              <NhanX nhan={x} x={goc + rongO / 2} y={H - 14} rongO={rongO} />
            </g>
          );
        })}
      </svg>
      <ChuThich ten={ls} />
    </>
  );
}

function ChuThich({ ten }: { ten: string[] }) {
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
      {ten.map((t, i) => (
        <span key={t} className="flex items-center gap-1 text-xs text-content-secondary">
          <span className="size-2 rounded-sm" style={{ background: mau(i) }} />
          {t}
        </span>
      ))}
    </div>
  );
}

function Duong({
  rows,
  dang,
  vung,
  nhieu,
}: {
  rows: Hang[];
  dang: Dang;
  vung?: boolean;
  nhieu?: boolean;
}) {
  const { day } = useDinhDang();
  const xk = dang.x ?? '';
  const yk = dang.y ?? '';
  const lk = dang.loat ?? '';
  const g = nhieu && lk ? gom(rows, xk, lk, yk) : null;
  const xs = g ? g.xs : rows.map((r) => chu(r[xk]));
  const ls = g ? g.ls : [yk];
  const layGT = (x: string, l: string, i: number) =>
    g ? g.lay(x, l) : so(rows[i]?.[yk]);

  const max = Math.max(...xs.flatMap((x, i) => ls.map((l) => layGT(x, l, i))), 0);
  const moc = mocTruc(max);
  const tran = moc[moc.length - 1] || 1;
  const rong = W - LE.trai - LE.phai;
  const cao = H - LE.tren - LE.duoi;
  const buoc = xs.length > 1 ? rong / (xs.length - 1) : 0;
  const toaDo = (i: number, v: number) => [LE.trai + i * buoc, LE.tren + cao - (v / tran) * cao];

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
        <Truc mocY={moc} cao={cao} rong={rong} />
        {ls.map((l, j) => {
          const diem = xs.map((x, i) => toaDo(i, layGT(x, l, i)));
          const d = diem.map(([px, py], i) => `${i ? 'L' : 'M'}${px},${py}`).join(' ');
          const mepPhai = diem[diem.length - 1]?.[0] ?? LE.trai;
          const mepTrai = diem[0]?.[0] ?? LE.trai;
          return (
            <g key={l}>
              {vung ? (
                <path d={`${d} L${mepPhai},${LE.tren + cao} L${mepTrai},${LE.tren + cao} Z`}
                      fill={mau(j)} opacity={0.14} />
              ) : null}
              <path d={d} fill="none" stroke={mau(j)} strokeWidth={2}
                    strokeLinejoin="round" strokeLinecap="round" />
              {diem.map(([px, py], i) => {
                const nhanX = xs[i] ?? '';
                return (
                <circle key={i} cx={px} cy={py} r={3} fill={mau(j)}>
                  <title>{`${nhanX}${ls.length > 1 ? ` · ${l}` : ''}: ${day(layGT(nhanX, l, i))}`}</title>
                </circle>
                );
              })}
            </g>
          );
        })}
        {xs.map((x, i) => (
          <NhanX key={x} nhan={x} x={LE.trai + i * buoc} y={H - 14} rongO={buoc || rong} />
        ))}
      </svg>
      {ls.length > 1 ? <ChuThich ten={ls} /> : null}
    </>
  );
}

function TronVanh({ rows, dang, vanh }: { rows: Hang[]; dang: Dang; vanh?: boolean }) {
  const { gon: gonSo, day } = useDinhDang();
  const xk = dang.x ?? '';
  const yk = dang.y ?? '';
  const gt = rows.map((r) => so(r[yk]));
  const tong = gt.reduce((s, v) => s + v, 0) || 1;
  const R = 92;
  const r0 = vanh ? 52 : 0;
  const cx = 120;
  const cy = 110;
  let goc = -Math.PI / 2;

  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg viewBox="0 0 240 220" className="w-[240px] shrink-0" role="img">
        {rows.map((r, i) => {
          const v = gt[i] ?? 0;
          const cung = (v / tong) * Math.PI * 2;
          const g1 = goc;
          const g2 = goc + cung;
          goc = g2;
          const lon = cung > Math.PI ? 1 : 0;
          const [x1, y1] = [cx + R * Math.cos(g1), cy + R * Math.sin(g1)];
          const [x2, y2] = [cx + R * Math.cos(g2), cy + R * Math.sin(g2)];
          const [x3, y3] = [cx + r0 * Math.cos(g2), cy + r0 * Math.sin(g2)];
          const [x4, y4] = [cx + r0 * Math.cos(g1), cy + r0 * Math.sin(g1)];
          const d = r0
            ? `M${x1},${y1} A${R},${R} 0 ${lon} 1 ${x2},${y2} L${x3},${y3} A${r0},${r0} 0 ${lon} 0 ${x4},${y4} Z`
            : `M${cx},${cy} L${x1},${y1} A${R},${R} 0 ${lon} 1 ${x2},${y2} Z`;
          return (
            <path key={i} d={d} fill={mau(i)}>
              <title>{`${chu(r[xk])}: ${day(v)} (${((v / tong) * 100).toFixed(1)}%)`}</title>
            </path>
          );
        })}
      </svg>
      <div className="min-w-0 flex-1 space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-baseline gap-2 text-xs">
            <span className="size-2 shrink-0 rounded-sm" style={{ background: mau(i) }} />
            <span className="min-w-0 flex-1 truncate text-content-secondary">{chu(r[xk])}</span>
            <span className="tabular-nums text-content-secondary">{gonSo(gt[i] ?? 0)}</span>
            <span className="w-12 text-right tabular-nums text-content-muted">
              {(((gt[i] ?? 0) / tong) * 100).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhanTan({ rows, dang, bong }: { rows: Hang[]; dang: Dang; bong?: boolean }) {
  const { day } = useDinhDang();
  const xk = dang.x ?? '';
  const yk = dang.y ?? '';
  const kk = dang.kich_thuoc ?? '';
  const xs = rows.map((r) => so(r[xk]));
  const ys = rows.map((r) => so(r[yk]));
  const ks = bong && kk ? rows.map((r) => so(r[kk])) : [];
  const mx = Math.max(...xs, 0) || 1;
  const my = Math.max(...ys, 0) || 1;
  const mk = Math.max(...ks, 0) || 1;
  const mocY = mocTruc(my);
  const tranY = mocY[mocY.length - 1] || 1;
  const rong = W - LE.trai - LE.phai;
  const cao = H - LE.tren - LE.duoi;
  const nhomK = dang.loat ?? '';
  const nhom: string[] = [];
  if (nhomK) for (const r of rows) if (!nhom.includes(chu(r[nhomK]))) nhom.push(chu(r[nhomK]));

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
        <Truc mocY={mocY} cao={cao} rong={rong} />
        {rows.map((r, i) => {
          const xv = xs[i] ?? 0;
          const yv = ys[i] ?? 0;
          const kv = ks[i] ?? 0;
          const px = LE.trai + (xv / mx) * rong;
          const py = LE.tren + cao - (yv / tranY) * cao;
          const j = nhomK ? nhom.indexOf(chu(r[nhomK])) : 0;
          const bk = bong && kk ? 4 + (kv / mk) * 16 : 4;
          return (
            <circle key={i} cx={px} cy={py} r={bk} fill={mau(j)} opacity={0.62}>
              <title>{`${xk}=${day(xv)} · ${yk}=${day(yv)}${
                bong && kk ? ` · ${kk}=${day(kv)}` : ''
              }`}</title>
            </circle>
          );
        })}
        <text x={LE.trai + rong / 2} y={H - 6} textAnchor="middle"
              className="fill-[var(--text-muted)] text-xs">{xk}</text>
      </svg>
      {nhom.length > 1 ? <ChuThich ten={nhom} /> : null}
    </>
  );
}

function Nhiet({ rows, dang }: { rows: Hang[]; dang: Dang }) {
  const { day } = useDinhDang();
  const { xs, ls, lay } = gom(rows, dang.x ?? '', dang.loat ?? '', dang.y ?? '');
  const gt = xs.flatMap((x) => ls.map((l) => lay(x, l)));
  const max = Math.max(...gt, 0) || 1;
  const min = Math.min(...gt, 0);
  const oW = Math.max(26, Math.min(70, (W - 140) / xs.length));
  const oH = 26;
  return (
    <svg viewBox={`0 0 ${140 + xs.length * oW} ${ls.length * oH + 46}`} className="w-full" role="img">
      {ls.map((l, j) => (
        <text key={l} x={132} y={j * oH + oH / 2 + 4 + 8} textAnchor="end"
              className="fill-[var(--text-primary)] text-xs">
          {l.length > 18 ? `${l.slice(0, 17)}…` : l}
          <title>{l}</title>
        </text>
      ))}
      {ls.map((l, j) =>
        xs.map((x, i) => {
          const v = lay(x, l);
          // Độ đậm theo tỉ lệ trong khoảng thật, không theo giá trị tuyệt đối:
          // một bảng toàn số lớn mà chênh nhau ít sẽ thành một mảng đặc kín và
          // không nói được gì.
          const t = (v - min) / (max - min || 1);
          return (
            <rect key={`${x}-${l}`} x={140 + i * oW} y={j * oH + 8} width={oW - 2} height={oH - 2}
                  rx={2} fill={mau(0)} opacity={0.08 + t * 0.9}>
              <title>{`${x} · ${l}: ${day(v)}`}</title>
            </rect>
          );
        }),
      )}
      {xs.map((x, i) => (
        <NhanX key={x} nhan={x} x={140 + i * oW + oW / 2} y={ls.length * oH + 26} rongO={oW} />
      ))}
    </svg>
  );
}

function PhanPhoi({ rows, dang }: { rows: Hang[]; dang: Dang }) {
  const { t } = useT();
  const { gon: gonSo } = useDinhDang();
  const yk = dang.y ?? '';
  // Quy tắc căn bậc hai, chặn 4..20 — xem `phanO`.
  const { min, buoc, dem } = phanO(rows.map((r) => so(r[yk])));
  const soO = dem.length;
  const moc = mocTruc(Math.max(...dem));
  const tran = moc[moc.length - 1] || 1;
  const rong = W - LE.trai - LE.phai;
  const cao = H - LE.tren - LE.duoi;
  const oW = rong / soO;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
      <Truc mocY={moc} cao={cao} rong={rong} dinhDang={(n) => String(n)} />
      {dem.map((c, i) => {
        const h = (c / tran) * cao;
        return (
          <g key={i}>
            <rect x={LE.trai + i * oW + 1} y={LE.tren + cao - h} width={oW - 2}
                  height={Math.max(h, 0)} fill={mau(0)}>
              <title>{t('troLyAiHoiBieuDo.dongPhanPhoi', { tu: gonSo(min + i * buoc), den: gonSo(min + (i + 1) * buoc), n: c })}</title>
            </rect>
            {i % Math.ceil(soO / 6) === 0 ? (
              <NhanX nhan={gonSo(min + i * buoc)} x={LE.trai + i * oW} y={H - 14} rongO={oW * 3} />
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

function Thac({ rows, dang }: { rows: Hang[]; dang: Dang }) {
  const { day } = useDinhDang();
  const xk = dang.x ?? '';
  const yk = dang.y ?? '';
  const gt = rows.map((r) => so(r[yk]));
  let luy = 0;
  const doan = gt.map((v) => {
    const tu = luy;
    luy += v;
    return [tu, luy] as const;
  });
  const dinh = Math.max(...doan.flat(), 0);
  const day_ = Math.min(...doan.flat(), 0);
  const moc = mocTruc(dinh);
  const tran = moc[moc.length - 1] || 1;
  const rong = W - LE.trai - LE.phai;
  const cao = H - LE.tren - LE.duoi;
  const oW = rong / rows.length;
  const yOf = (v: number) => LE.tren + cao - ((v - day_) / (tran - day_ || 1)) * cao;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
      <Truc mocY={moc} cao={cao} rong={rong} />
      {rows.map((r, i) => {
        const [a, b] = doan[i] ?? [0, 0];
        const g = gt[i] ?? 0;
        const y1 = yOf(Math.max(a, b));
        const y2 = yOf(Math.min(a, b));
        return (
          <g key={i}>
            <rect x={LE.trai + i * oW + oW * 0.18} y={y1} width={oW * 0.64}
                  height={Math.max(y2 - y1, 1)} rx={2}
                  fill={g >= 0 ? 'var(--color-success-600)' : 'var(--color-danger-600)'}>
              <title>{`${chu(r[xk])}: ${g >= 0 ? '+' : ''}${day(g)} → ${day(b)}`}</title>
            </rect>
            <NhanX nhan={chu(r[xk])} x={LE.trai + i * oW + oW / 2} y={H - 14} rongO={oW} />
          </g>
        );
      })}
    </svg>
  );
}

function Phieu({ rows, dang }: { rows: Hang[]; dang: Dang }) {
  const { t } = useT();
  const { gon: gonSo, day } = useDinhDang();
  const xk = dang.x ?? '';
  const yk = dang.y ?? '';
  const gt = rows.map((r) => so(r[yk]));
  const max = Math.max(...gt, 0) || 1;
  const oH = 34;
  return (
    <svg viewBox={`0 0 ${W} ${rows.length * oH + 12}`} className="w-full" role="img">
      {rows.map((r, i) => {
        const g = gt[i] ?? 0;
        const w = (g / max) * (W - 220);
        const x = 150 + (W - 220 - w) / 2;
        return (
          <g key={i}>
            <text x={142} y={i * oH + oH / 2 + 4} textAnchor="end"
                  className="fill-[var(--text-primary)] text-xs">
              {chu(r[xk]).length > 18 ? `${chu(r[xk]).slice(0, 17)}…` : chu(r[xk])}
              <title>{chu(r[xk])}</title>
            </text>
            <rect x={x} y={i * oH + 4} width={Math.max(w, 2)} height={oH - 10} rx={3} fill={mau(i)}>
              <title>{`${chu(r[xk])}: ${day(g)}${
                i ? ` · ${t('troLyAiHoiBieuDo.soVoiBuocDau', { pt: ((g / (gt[0] || 1)) * 100).toFixed(1) })}` : ''
              }`}</title>
            </rect>
            <text x={W - 58} y={i * oH + oH / 2 + 4}
                  className="fill-[var(--text-secondary)] text-xs tabular-nums">{gonSo(g)}</text>
          </g>
        );
      })}
    </svg>
  );
}

function Radar({ rows, dang }: { rows: Hang[]; dang: Dang }) {
  const { day } = useDinhDang();
  const xk = dang.x ?? '';
  const ct = dang.chi_tieu ?? [];
  const cx = 170;
  const cy = 150;
  const R = 108;
  // Chuẩn hoá TỪNG chỉ tiêu: các trục có đơn vị khác nhau, và ép chung một
  // thang sẽ làm chỉ tiêu số lớn nuốt hết hình.
  const tran = ct.map((c) => Math.max(...rows.map((r) => so(r[c])), 0) || 1);
  const diem = (i: number, t: number) => {
    const g = -Math.PI / 2 + (i / ct.length) * Math.PI * 2;
    return [cx + R * t * Math.cos(g), cy + R * t * Math.sin(g)];
  };
  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg viewBox="0 0 340 300" className="w-[340px] shrink-0" role="img">
        {[0.25, 0.5, 0.75, 1].map((t) => (
          <polygon key={t} className="fill-none stroke-[var(--border-default)]"
                   points={ct.map((_, i) => diem(i, t).join(',')).join(' ')} />
        ))}
        {ct.map((c, i) => {
          const [px, py] = diem(i, 1.16);
          return (
            <text key={c} x={px} y={py} textAnchor="middle"
                  className="fill-[var(--text-secondary)] text-xs">
              {c.length > 12 ? `${c.slice(0, 11)}…` : c}
              <title>{c}</title>
            </text>
          );
        })}
        {rows.map((r, j) => (
          <polygon key={j} fill={mau(j)} fillOpacity={0.18} stroke={mau(j)} strokeWidth={2}
                   points={ct.map((c, i) => diem(i, so(r[c]) / (tran[i] ?? 1)).join(',')).join(' ')}>
            <title>{`${chu(r[xk])}: ${ct.map((c) => `${c}=${day(so(r[c]))}`).join(' · ')}`}</title>
          </polygon>
        ))}
      </svg>
      <ChuThich ten={rows.map((r) => chu(r[xk]))} />
    </div>
  );
}

// ── Cửa vào ────────────────────────────────────────────────────────────────

const DANG = [
  'cot', 'cot_ngang', 'cot_nhom', 'cot_chong', 'cot_chong_100', 'duong', 'duong_nhieu', 'vung', 'tron', 'vanh',
  'phan_tan', 'bong_bong', 'nhiet', 'phan_phoi', 'thac', 'phieu', 'radar', 'so_lon',
] as const;
type MaDang = (typeof DANG)[number];

/** Dạng nào là BIỂU ĐỒ — phần còn lại (`bang`, `trong`) do bảng lo. */
export const LA_BIEU_DO: ReadonlySet<string> = new Set(DANG);
const laDang = (k: string): k is MaDang => LA_BIEU_DO.has(k);

export function BieuDo({ rows, dang }: { rows: Hang[]; dang: AskDisplay }) {
  const { t, locale } = useT();
  const [mo, setMo] = useState(true);
  // ── ĐỔI DẠNG TẠI CHỖ ── không có ô này thì muốn xem dạng khác phải GÕ LẠI câu hỏi (chạy lại SQL và một lượt gọi mô
  // hình). `null` = dạng máy chủ chọn — hỏi lượt mới thì tự quay về đề xuất của máy chủ.
  const [chon, setChon] = useState<string | null>(null);

  const dinhDang = useMemo(() => {
    const dv = { ty: t('troLyAiHoiBieuDo.ty'), trieu: t('troLyAiHoiBieuDo.trieu'), nghin: t('troLyAiHoiBieuDo.nghin') };
    return {
      gon: (n: number) => gonSoThuan(n, locale, dv),
      day: (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 4 }),
    };
  }, [t, locale]);

  const hopLe = dang.hopLe ?? [];
  const dangVe = chon && hopLe.includes(chon) ? chon : dang.dang;
  // Trục lấy theo dạng ĐANG VẼ — giữ trục của dạng máy chủ chọn sẽ cho một biểu đồ đúng kiểu và sai cột.
  const d: Dang = chon && dang.truc?.[chon] ? { ...dang, ...dang.truc[chon], dang: chon } : dang;

  const noiDung = useMemo(() => {
    switch (dangVe) {
      case 'so_lon': return <SoLon rows={rows} dang={d} />;
      case 'cot': return <Cot rows={rows} dang={d} />;
      case 'cot_ngang': return <Cot rows={rows} dang={d} ngang />;
      case 'cot_nhom': return <CotNhieuLoat rows={rows} dang={d} kieu="nhom" />;
      case 'cot_chong': return <CotNhieuLoat rows={rows} dang={d} kieu="chong" />;
      case 'cot_chong_100': return <CotNhieuLoat rows={rows} dang={d} kieu="chong100" />;
      case 'duong': return <Duong rows={rows} dang={d} />;
      case 'duong_nhieu': return <Duong rows={rows} dang={d} nhieu />;
      case 'vung': return <Duong rows={rows} dang={d} vung />;
      case 'tron': return <TronVanh rows={rows} dang={d} />;
      case 'vanh': return <TronVanh rows={rows} dang={d} vanh />;
      case 'phan_tan': return <PhanTan rows={rows} dang={d} />;
      case 'bong_bong': return <PhanTan rows={rows} dang={d} bong />;
      case 'nhiet': return <Nhiet rows={rows} dang={d} />;
      case 'phan_phoi': return <PhanPhoi rows={rows} dang={d} />;
      case 'thac': return <Thac rows={rows} dang={d} />;
      case 'phieu': return <Phieu rows={rows} dang={d} />;
      case 'radar': return <Radar rows={rows} dang={d} />;
      default: return null;
    }
  }, [rows, d, dangVe]);

  if (!noiDung || !rows.length) return null;

  return (
    <DinhDangCtx.Provider value={dinhDang}>
      <section className="mt-3 rounded-lg border border-default p-3">
        <button
          type="button"
          aria-expanded={mo}
          onClick={() => setMo((v) => !v)}
          className="mb-1 flex w-full items-center gap-2 text-start"
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
            {t('troLyAiHoiBieuDo.tieuDe')}
          </span>
          <span className="flex-1" />
          <span className="text-xs text-content-muted">{mo ? t('troLyAiHoiBieuDo.an') : t('troLyAiHoiBieuDo.hien')}</span>
        </button>
        {mo ? (
          <>
            {/* Ô chọn chỉ liệt kê dạng máy chủ nói là VẼ ĐƯỢC — cho chọn dạng không hợp là một hình trống hay sai, và
                người dùng sẽ đổ lỗi cho dữ liệu. */}
            {hopLe.length > 1 ? (
              <div role="group" aria-label={t('troLyAiHoiBieuDo.doiDang')} className="mb-2 flex flex-wrap items-center gap-1">
                {hopLe.map((k) => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={k === dangVe}
                    onClick={() => setChon(k === dang.dang ? null : k)}
                    className={cn(
                      'rounded px-2 py-1 text-xs',
                      k === dangVe
                        ? 'bg-primary-100 font-medium text-primary-700'
                        : 'bg-subtle text-content-secondary hover:text-content-primary',
                    )}
                  >
                    {laDang(k) ? t(`troLyAiHoiBieuDo.${k}`) : k}
                  </button>
                ))}
              </div>
            ) : null}
            {noiDung}
            {/* `vi_sao` là của dạng MÁY CHỦ chọn — chỉ hiện khi người dùng chưa tự đổi; đây là chỗ duy nhất nói ra
                rằng dạng đã bị đổi so với thứ họ xin. */}
            {chon === null && dang.vi_sao ? <Chu>{dang.vi_sao}</Chu> : null}
          </>
        ) : null}
      </section>
    </DinhDangCtx.Provider>
  );
}
